import React, { useEffect, useState } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Slide } from '@wds/motion';
import { Checkbox } from '@wds/checkbox';
import { FormField, FormGroup } from '@wds/form-group';
import { Radio } from '@wds/radio';

import { getCookie, isSafeRedirectUrl } from 'sma.common/utils';

import PropTypes from 'prop-types';
import { ProgressCircle } from '@wds/progress-circle';
import STRINGS from '../constants';

const Challenge = (props) => {
	const {
		currentPage,
		setCurrentPage,
		isLoaded,
		context,
		setContext,
		error,
		setError,
		isSliding,
		setIsSliding,
		handleError,
		nextUrl,
		method,
		message,
		isChange,
		sending,
		cooldown,
		sendEmailCode,
	} = props;

	const [isValid, setIsValid] = useState(false);
	const [newMethod, setNewMethod] = useState('totp');

	useEffect(() => {
		if (context.challengeCode.match(/^[0-9]+$/) && context.challengeCode.length === 6) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}
	}, [context]);

	const onSelectNewMethod = (e) => {
		setNewMethod(e.target.value);
	};

	const onHandleContextChange = (key) => (e) => {
		setContext({ ...context, [key]: e.target.value });
	};

	const submitChallengeCode = async () => {
		setError('');
		try {
			let url;
			let action;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'begin_device_change';
			} else {
				url = reverse('api-internal-mfa-challenge');
				action = method === 'totp' ? 'verify_totp_challenge' : 'verify_email_challenge';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({
					action,
					method: isChange ? newMethod : method,
					challenge_code: context.challengeCode,
					remember_device: context.rememberDevice,
				}),
			});
			const data = await response.json();
			if (isChange) {
				if (data.success) {
					setError('');
					let newContext = { ...context };
					if (data.method.method === 'totp') {
						newContext = {
							...newContext,
							challengeCode: '',
							new_method: newMethod,
							method_id: data.method.id,
							qrCodeString: data.qr_code,
							securityKeyString: data.secret,
						};
					} else if (data.method.method === 'email') {
						newContext = {
							...newContext,
							challengeCode: '',
							new_method: newMethod,
							method_id: data.method.id,
							status: 'code_pending',
						};
					}
					setContext(newContext);
					if (data.method.method === 'totp') {
						setCurrentPage('totp-enrollment');
					} else if (data.method.method === 'email') {
						setCurrentPage('email-enrollment');
					}
				}
				if (data.error) {
					if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
						window.location.href = reverse('mfa-lockout');
					}
					if (data.error === 'code_already_pending') {
						setCurrentPage('email-enrollment');
					}
					setError(data.error);
				}
			}
			if (!isChange) {
				if (data.success) {
					setError('');
					if (data.next_url) {
						window.location.href = data.next_url;
					} else if (isSafeRedirectUrl(nextUrl)) {
						window.location.href = nextUrl;
					} else {
						window.location.href = '/';
					}
				} else if (data.error) {
					if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
						window.location.href = reverse('mfa-lockout');
					} else {
						setError(data.error);
					}
				}
			}
		} catch (err) {
			handleError(`Error submitting challenge code: ${err}`);
		}
	};

	const inputColor = error ? 'error' : '';

	const handleCheckboxChange = (event) => {
		setContext({ ...context, rememberDevice: event.target.checked });
	};

	return (
		<Slide
			in={isLoaded && currentPage === 'challenge'}
			speed={currentPage === 'challenge' ? 'slow' : undefined}
			timeout={currentPage === 'challenge' ? undefined : 150}
			direction='right'
			onEnter={() => setIsSliding(true)}
			onExited={() => setIsSliding(false)}
			alwaysRenderContent
		>
			<Box
				display='flex'
				flexDirection='column'
				gap={4}
				style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}
			>
				{sending && (
					<Box
						display='flex'
						flexDirection='column'
						justifyContent='center'
						alignItems='center'
						gap={2}
						mt={9}
					>
						<Box display='flex' justifyContent='center' alignItems='center'>
							<ProgressCircle continuous />
						</Box>
					</Box>
				)}
				{!sending && (
					<>
						<Box
							display='flex'
							flexDirection='column'
							justifyContent='center'
							alignItems='center'
							gap={2}
						>
							{isChange && (
								method !== 'email' ? (
									<FormGroup>
										<FormField>
											<Typography variant='bodySm' align='center'>
												{STRINGS.whatMethod}
											</Typography>
										</FormField>
										<FormField>
											<Radio
												label={STRINGS.authenticatorAppTOTP}
												onChange={onSelectNewMethod}
												name='mfaMethodSelect'
												value='totp'
												checked={newMethod === 'totp'}
											/>
										</FormField>
										<FormField>
											<Radio
												label={STRINGS.email}
												onChange={onSelectNewMethod}
												name='mfaMethodSelect'
												value='email'
												checked={newMethod === 'email'}
											/>
										</FormField>
									</FormGroup>
								) : (
									<Typography variant='bodySm' align='center'>
										{STRINGS.totpOnly}
									</Typography>
								)
							)}
							<Typography variant='sectionTitleSm' align='center' weight='medium'>
								{isChange ? STRINGS.enterCodeToContinue : STRINGS.enterCodeToLogin}
							</Typography>
							{isChange && method === 'totp' && (
								<Typography variant='bodySm' align='center'>
									{STRINGS.beforeChangingDevice}
								</Typography>
							)}
							{isChange && method === 'email' && (
								<Typography variant='bodySm' align='center'>
									{STRINGS.beforeChangingDeviceEmail}
								</Typography>
							)}
							{!isChange && method === 'totp' && (
								<Typography variant='bodySm' align='center'>
									{STRINGS.checkApp}
								</Typography>
							)}
							{!isChange && method === 'email' && (
								<Typography variant='bodySm' align='center'>
									{STRINGS.checkEmail}
								</Typography>
							)}
							{method === 'email' && (
								<Button
									onClick={() => sendEmailCode()}
									id='start-resend-email-button'
									size='lg'
									aria-label={STRINGS.resendCode}
									disabled={cooldown > 0}
								>
									{cooldown > 0 ? `${STRINGS.resendCode} (${cooldown})` : (STRINGS.sendCode)}
								</Button>
							)}
						</Box>
						<Box
							display='flex'
							flexDirection='column'
							gap={4}
							pb={6}
						>
							{error && (
								<Box
									p={4}
									style={{
										backgroundColor: '#AC145A',
										borderRadius: '5px',
										color: '#FFFFFF',
									}}
									role='alert'
									aria-live='assertive'
								>
									{STRINGS?.[error] || error}
								</Box>
							)}
							{message && (
								<Box
									p={4}
									style={{
										backgroundColor: '#42ac14',
										borderRadius: '5px',
										color: '#FFFFFF',
									}}
									role='alert'
									aria-live='assertive'
								>
									{STRINGS?.[message] || message}
								</Box>
							)}
							<Input
								label={STRINGS.enterCodeProvided}
								value={context.challengeCode}
								placeholder={STRINGS.enterCodeProvided}
								onChange={onHandleContextChange('challengeCode')}
								id='mfa-challenge-code'
								type='text'
								color={inputColor}
								stretched
								aria-required='true'
								autoFocus
								required
							/>
							{!isChange && (
								<Box mt={4}>
									<Checkbox
										label={STRINGS.rememberDevice}
										onChange={handleCheckboxChange}
										aria-label={STRINGS.rememberDevice}
									/>
								</Box>
							)}
							<Button
								id='next-button'
								size='lg'
								disabled={!isValid}
								aria-disabled={!isValid}
								onClick={submitChallengeCode}
							>
								{STRINGS.continue}
							</Button>
						</Box>
					</>
				)}
			</Box>
		</Slide>
	);
};

Challenge.propTypes = {
	isLoaded: PropTypes.bool.isRequired,
	context: PropTypes.object.isRequired,
	setContext: PropTypes.func.isRequired,
	error: PropTypes.string,
	setError: PropTypes.func.isRequired,
	isSliding: PropTypes.bool.isRequired,
	setIsSliding: PropTypes.func.isRequired,
	handleError: PropTypes.func.isRequired,
	currentPage: PropTypes.string.isRequired,
	setCurrentPage: PropTypes.func.isRequired,
	nextUrl: PropTypes.string,
	method: PropTypes.string.isRequired,
	isChange: PropTypes.bool,
	sending: PropTypes.bool,
	message: PropTypes.string.isRequired,
	cooldown: PropTypes.number.isRequired,
	sendEmailCode: PropTypes.func.isRequired,
};

Challenge.defaultProps = {
	error: '',
	nextUrl: '',
	isChange: false,
	sending: false,
};

export default Challenge;
